import type {FetchResult} from "./types/fetch-result.interface";

/**
 * Executor that fetches a contiguous range of values. The origin is bound into
 * this closure by the Axis (it wraps dataService.fetchValues(origin, offset, limit)),
 * so the coordinator stays free of any origin/cache knowledge.
 *
 * @template TValue The type of the values being fetched.
 */
export type RangeFetchExecutor<TValue> = (offset: number, limit: number) => Promise<FetchResult<TValue>>;

/**
 * Synchronous post-fetch step owned by the Axis. Runs boundary update THEN cache
 * insert. The coordinator invokes it but never touches Axis cache/boundary state
 * itself - cache/boundary mutation stays owned by Axis.
 *
 * @template TValue The type of the values being applied.
 */
export type ApplyFetchResult<TValue> = (result: FetchResult<TValue>, offset: number) => void;

/**
 * Internal record tracking an in-flight fetch+insert for a contiguous range.
 * Concurrent ensureRange() callers dedup against these and await the ones
 * overlapping their requested range.
 *
 * @template TValue The type of the values being fetched.
 */
interface PendingFetch {
    /** Inclusive start index of the range being fetched (relative to origin). */
    start: number;
    /** Exclusive end index of the range being fetched. */
    end: number;
    /** Resolves only AFTER the fetched values are applied (boundaries + insert). */
    promise: Promise<void>;
}

/**
 * Coordinates in-flight range fetches for an Axis (extracted from Axis per PRD-079).
 *
 * Owns ONLY the pending-fetch bookkeeping: dedup tracking, the tracked-promise
 * lifecycle (register-then-cleanup on settle), a read API for missing-range
 * detection, and awaiting every fetch overlapping a requested range. Cache and
 * boundary mutation are NOT its concern - they happen inside the injected
 * applyResult callback, owned by the Axis.
 *
 * The register-then-snapshot atomicity that makes concurrent dedup safe lives in
 * the caller (Axis.ensureRange): startFetch (synchronous register) and
 * awaitOverlapping (synchronous snapshot of the pending list, then await) must be
 * called with no intervening await, so a concurrent caller always sees in-flight
 * fetches started just before it.
 *
 * @template TValue The type of the values managed by the coordinated fetches.
 */
export class RangeFetchCoordinator<TValue> {

    /**
     * Tracks in-flight fetch+apply operations to prevent duplicate fetches when
     * concurrent callers overlap. Each entry's promise resolves only AFTER its
     * values are applied, so a caller that dedups against an in-flight entry and
     * awaits it is guaranteed the data is visible. Entries are removed when their
     * fetch settles (success or error).
     */
    private readonly pendingFetches: PendingFetch[] = [];

    /**
     * Creates a coordinator wired to its Axis collaborators via composition.
     *
     * @param fetchValues - Executor fetching a [offset, offset+limit) range.
     * @param applyResult - Synchronous post-fetch step (boundaries + insert).
     */
    constructor(
        private readonly fetchValues: RangeFetchExecutor<TValue>,
        private readonly applyResult: ApplyFetchResult<TValue>
    ) {}

    /**
     * Read API for dedup: reports whether an index is currently covered by an
     * in-flight fetch. Used by Axis.findMissingRanges instead of reaching into
     * raw coordinator state.
     *
     * @param index - Index to check (relative to origin).
     * @returns true if an in-flight fetch spans the index (start inclusive, end exclusive).
     */
    coversIndex(index: number): boolean {
        return this.pendingFetches.some((pf) => index >= pf.start && index < pf.end);
    }

    /**
     * Starts a fetch+apply for one missing range as a single tracked promise.
     * The entry is registered in pendingFetches so concurrent callers dedup
     * against it, and removed once the fetch+apply has settled (resolve OR reject).
     *
     * @param start - Inclusive start index of the missing range.
     * @param end - Exclusive end index of the missing range.
     */
    startFetch(start: number, end: number): void {
        const entry: PendingFetch = {start, end, promise: Promise.resolve()};

        entry.promise = this.fetchAndApply(start, end).finally(() => {
            const idx = this.pendingFetches.indexOf(entry);
            if (idx !== -1) {
                this.pendingFetches.splice(idx, 1);
            }
        });

        this.pendingFetches.push(entry);
    }

    /**
     * Awaits every in-flight fetch overlapping [start, end) - including those
     * started by concurrent callers - so the caller resolves only after all data
     * covering its range is applied. A rejection propagates to every awaiter whose
     * snapshot included the failing fetch.
     *
     * @param start - Inclusive start index of the awaited range.
     * @param end - Exclusive end index of the awaited range.
     */
    async awaitOverlapping(start: number, end: number): Promise<void> {
        const overlapping = this.pendingFetches
            .filter((pf) => pf.start < end && pf.end > start)
            .map((pf) => pf.promise);

        await Promise.all(overlapping);
    }

    /**
     * Fetches a single missing range and applies the result. The apply happens
     * INSIDE this promise, so any caller awaiting it is guaranteed the data is
     * visible on resolution.
     *
     * @param start - Inclusive start index / relative offset of the fetch.
     * @param end - Exclusive end index of the fetch.
     */
    private async fetchAndApply(start: number, end: number): Promise<void> {
        const limit = end - start;
        const result = await this.fetchValues(start, limit);

        this.applyResult(result, start);
    }
}
